import React, { useState, useEffect, useCallback, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../contexts/ToastContext";
import GettingStartedService from "../../../lib/gettingStartedService";

const GettingStarted = ({ hideWhenCompleted = false }) => {
  const { user } = useAuth();
  const { add } = useToast();

  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [completedSteps, setCompletedSteps] = useState([]);

  // Step definitions with actions
  const stepDefinitions = {
    1: { action: null, description: "Sign up and verify your email address" },
    2: {
      action: "Generate API Key",
      description: "Create your first API key for authentication",
    },
    3: {
      action: "Try API Call",
      description: "Test the connection with a simple API request",
    },
    4: {
      action: "Setup Webhooks",
      description: "Set up webhooks to receive real-time events",
    },
    5: {
      action: "Download SDK",
      description: "Download and integrate our SDK in your application",
    },
    6: {
      action: "Deploy Guide",
      description: "Deploy your integration to production",
    },
  };

  const progressData = useMemo(() => {
    const totalSteps = steps.length;
    const completedCount = completedSteps.length;
    const progressPercentage =
      totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;
    return { totalSteps, completedCount, progressPercentage };
  }, [steps.length, completedSteps.length]);

  // Load user's getting started progress
  const loadProgress = useCallback(
    async (showLoading = true) => {
      if (!user?.id) return;

      if (showLoading) setLoading(true);

      try {
        const result = await GettingStartedService.getUserProgress(user.id);

        if (result.success && result.data) {
          const steps = result.data.steps || [];
          const stepsWithActions = steps.map((step) => ({
            ...step,
            action: stepDefinitions[step.id]?.action || null,
            description: stepDefinitions[step.id]?.description || step.title,
          }));

          setSteps(stepsWithActions);
          setCompletedSteps(
            stepsWithActions
              .filter((step) => step.completed)
              .map((step) => step.id)
          );
        } else if (result.error) {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error loading progress:", error);
        if (showLoading) {
          add({
            title: "Error",
            description: "Failed to load getting started progress",
            variant: "destructive",
          });
        }
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [user?.id, add],
  );

  // Initialize progress for new users
  const initializeProgress = async () => {
    if (!user?.id) return;

    try {
      await GettingStartedService.initializeUserSteps(user.id);
      await loadProgress();
    } catch (error) {
      console.error("Error initializing progress:", error);
    }
  };

  // Mark step as complete
  const markStepComplete = useCallback(
    async (stepId) => {
      if (!user?.id || actionLoading[stepId]) return;

      setActionLoading((prev) => ({ ...prev, [stepId]: true }));

      try {
        const result = await GettingStartedService.markStepComplete(
          user.id,
          stepId
        );

        if (result.success) {
          // Update local state immediately for smooth UX
          setSteps((prevSteps) =>
            prevSteps.map((step) =>
              step.id === stepId
                ? {
                    ...step,
                    completed: true,
                    completedAt: result.data.completedAt,
                  }
                : step,
            ),
          );
          setCompletedSteps((prev) => [...prev, stepId]);

          // Check if all steps are now completed
          const updatedCompletedSteps = [...completedSteps, stepId];
          const allCompleted = updatedCompletedSteps.length === steps.length;

          // Dispatch events for real-time updates
          window.dispatchEvent(
            new CustomEvent("gettingStarted:stepCompleted", {
              detail: { stepId, userId: user.id },
            }),
          );

          if (allCompleted) {
            // All steps completed - dispatch completion event with delay
          }
        } else {
          throw new Error(result.error || "Failed to mark step as complete");
        }
      } catch (error) {
        console.error("Error completing step:", error);
        add({
          title: "Error",
          description: error.message || "Failed to mark step as complete",
          variant: "destructive",
        });
      }
    },
    [user?.id, loadProgress, add]
  );

  // Handle step action (navigations, modals, etc.)
  const handleStepAction = async (stepId, action) => {
    console.log(`Action: ${action} for step ${stepId}`);

    switch (action) {
      case "Generate API Key":
        // Navigate to API Keys tab or trigger modal
        // You can add navigation logic here
        add({
          title: "Info",
          description:
            "Navigate to API Keys section to generate your first key",
          variant: "default",
        });
        break;

      case "Try API Call":
        // Open API documentation or testing interface
        add({
          title: "Info",
          description: "Opening API documentation...",
          variant: "default",
        });
        window.open("/api-documentation", "_blank");
        break;

      case "Setup Webhooks":
        // Navigate to webhooks section
        add({
          title: "Info",
          description: "Navigate to Integration section to setup webhooks",
          variant: "default",
        });
        break;

      case "Download SDK":
        // Open SDK download page
        add({
          title: "Info",
          description: "Opening SDK download page...",
          variant: "default",
        });
        window.open("/api-documentation#sdks", "_blank");
        break;

      case "Deploy Guide":
        // Open deployment guide
        add({
          title: "Info",
          description: "Opening deployment guide...",
          variant: "default",
        });
        window.open("/api-documentation#deployment", "_blank");
        break;

      default:
        add({
          title: "Info",
          description: `Action: ${action}`,
          variant: "default",
        });
    }
  };

  // Reset progress (for testing/admin)
  const resetProgress = async () => {
    if (!user?.id) return;

    try {
      const result = await GettingStartedService.resetAllProgress(user.id);
      if (result.success) {
        await loadProgress();
        add({
          title: "Success",
          description: "Progress reset successfully",
          variant: "default",
        });
      } else {
        add({
          title: "Error",
          description: "Failed to reset progress: " + result.error,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error resetting progress:", error);
      add({
        title: "Error",
        description: "Failed to reset progress",
        variant: "destructive",
      });
    }
  };

  // Load progress on component mount and user change
  useEffect(() => {
    if (user?.id) {
      loadProgress(true);
    }
  }, [user?.id, loadProgress]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">
              Loading your progress...
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Hide component when all steps are completed (if hideWhenCompleted is true)
  if (hideWhenCompleted && completedCount === totalSteps && totalSteps > 0) {
    return null;
  }

  // No steps loaded
  if (!steps.length) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">
            No getting started steps found.
          </p>
          <Button onClick={initializeProgress} variant="outline">
            Initialize Getting Started
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Getting Started
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Complete these steps to get your integration up and running
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary">
            {progressData.completedCount}/{progressData.totalSteps}
          </p>
          <p className="text-xs text-muted-foreground">Steps completed</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-muted-foreground">Progress</span>
          <span className="text-sm font-medium text-foreground">
            {progressData.progressPercentage.toFixed(0)}%
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div
            className="bg-gradient-to-r from-primary to-secondary h-3 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressData.progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Steps List */}
      <div className="space-y-4">
        {steps.map((step, index) => {
          const isCompleted = step.completed;
          const isNext =
            !isCompleted &&
            (step.id === 1 || completedSteps.includes(step.id - 1));
          const isLocked = !isCompleted && !isNext && step.id > 1;

          return (
            <div
              key={step.id}
              className={`p-4 rounded-lg border transition-all duration-200 ${
                isCompleted
                  ? "bg-accent/5 border-accent/20"
                  : isNext
                    ? "bg-primary/5 border-primary/20"
                    : "bg-muted/30 border-border"
              }`}
            >
              <div className="flex items-start space-x-4">
                {/* Step Number/Status */}
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                    isCompleted
                      ? "bg-accent border-accent text-white"
                      : isNext
                        ? "bg-primary border-primary text-white"
                        : "bg-muted border-border text-muted-foreground"
                  }`}
                >
                  {isCompleted ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <span className="text-sm font-medium">{step.id}</span>
                  )}
                </div>

                {/* Step Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3
                        className={`font-medium mb-1 ${
                          isCompleted
                            ? "text-accent"
                            : isNext
                              ? "text-primary"
                              : "text-foreground"
                        }`}
                      >
                        {step.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {step.description}
                      </p>
                      {step.completedAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Completed on{" "}
                          {new Date(step.completedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {/* Action Button */}
                    <div className="ml-4">
                      {isCompleted ? (
                        <div className="flex items-center space-x-2 text-accent">
                          <Icon name="CheckCircle" size={16} />
                          <span className="text-sm font-medium">Completed</span>
                        </div>
                      ) : isNext && step.action ? (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleStepAction(step.id, step.action)}
                          className="text-xs"
                          disabled={actionLoading[step.id]}
                        >
                          {actionLoading[step.id] ? (
                            <div className="flex items-center space-x-2">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              <span>Loading...</span>
                            </div>
                          ) : (
                            step.action
                          )}
                        </Button>
                      ) : isLocked ? (
                        <div className="flex items-center space-x-2 text-muted-foreground">
                          <Icon name="Lock" size={16} />
                          <span className="text-sm">Locked</span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Mark as Complete Button for Testing */}
                  {isNext && !isCompleted && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Check"
                        iconPosition="left"
                        onClick={() => markStepComplete(step.id)}
                        className="text-xs text-muted-foreground hover:text-foreground"
                        disabled={actionLoading[step.id]}
                      >
                        {actionLoading[step.id]
                          ? "Completing..."
                          : "Mark as Complete"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Completion Message */}
      {progressData.completedCount === progressData.totalSteps &&
        progressData.totalSteps > 0 && (
          <div className="mt-6 p-4 bg-gradient-to-r from-accent/10 to-secondary/10 rounded-lg border border-accent/20">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-accent/20 rounded-full">
                <Icon name="Trophy" size={20} className="text-accent" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">
                  Congratulations!
                </h3>
                <p className="text-sm text-muted-foreground">
                  You've completed all setup steps. Your integration is ready
                  for production!
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Help Section */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-foreground mb-1">Need Help?</h3>
            <p className="text-sm text-muted-foreground">
              Check our documentation or contact support for assistance
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              iconName="BookOpen"
              iconPosition="left"
              onClick={() => (window.location.href = "/api-documentation")}
            >
              Documentation
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="MessageSquare"
              iconPosition="left"
            >
              Contact Support
            </Button>
            {/* Reset Progress Button (for development/testing) */}
            {process.env.NODE_ENV === "development" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetProgress}
                className="text-xs text-destructive hover:text-destructive"
              >
                Reset Progress
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStarted;
